/**
 * 2D polygon helpers in the horizontal (x, z) plane, shared by custom rooms and extruded shapes.
 * A "region" is a list of contours filled with the even-odd rule, so holes are simply extra contours.
 */

export type Pt = [number, number]
export type Contour = Pt[]

/** Signed area; positive when the contour runs counter-clockwise in (x, z) with x to the right and z up. */
export function signedArea(c: Contour): number {
  let a = 0
  for (let i = 0; i < c.length; i++) {
    const [x1, z1] = c[i]!
    const [x2, z2] = c[(i + 1) % c.length]!
    a += x1 * z2 - x2 * z1
  }
  return a / 2
}

/** A copy of the contour wound counter-clockwise, so the interior is always on the left of each edge. */
export function ccw(c: Contour): Contour {
  return signedArea(c) < 0 ? [...c].reverse() : c.map((p) => [...p] as Pt)
}

export function perimeter(c: Contour): number {
  let s = 0
  for (let i = 0; i < c.length; i++) {
    const a = c[i]!
    const b = c[(i + 1) % c.length]!
    s += Math.hypot(b[0] - a[0], b[1] - a[1])
  }
  return s
}

export function bounds(c: Contour): { minX: number; maxX: number; minZ: number; maxZ: number } {
  let minX = Infinity
  let maxX = -Infinity
  let minZ = Infinity
  let maxZ = -Infinity
  for (const [x, z] of c) {
    minX = Math.min(minX, x)
    maxX = Math.max(maxX, x)
    minZ = Math.min(minZ, z)
    maxZ = Math.max(maxZ, z)
  }
  return { minX, maxX, minZ, maxZ }
}

/** Translate so the bounding box is centred on the origin. */
export function recenter(c: Contour): { points: Contour; offset: Pt } {
  const b = bounds(c)
  const ox = (b.minX + b.maxX) / 2
  const oz = (b.minZ + b.maxZ) / 2
  return { points: c.map(([x, z]) => [x - ox, z - oz] as Pt), offset: [ox, oz] }
}

/** Even-odd point-in-polygon test for one contour. */
export function pointInContour(c: Contour, x: number, z: number): boolean {
  let inside = false
  for (let i = 0, j = c.length - 1; i < c.length; j = i++) {
    const [xi, zi] = c[i]!
    const [xj, zj] = c[j]!
    if (zi > z !== zj > z && x < ((xj - xi) * (z - zi)) / (zj - zi) + xi) inside = !inside
  }
  return inside
}

/** Even-odd across all contours: outer boundary plus holes. */
export function pointInRegion(contours: Contour[], x: number, z: number): boolean {
  let inside = false
  for (const c of contours) if (pointInContour(c, x, z)) inside = !inside
  return inside
}

/** Closest point on a segment, and the squared distance to it. */
function nearestOnSegment(a: Pt, b: Pt, x: number, z: number): { q: Pt; d2: number } {
  const dx = b[0] - a[0]
  const dz = b[1] - a[1]
  const l2 = dx * dx + dz * dz
  const t = l2 < 1e-18 ? 0 : Math.max(0, Math.min(1, ((x - a[0]) * dx + (z - a[1]) * dz) / l2))
  const q: Pt = [a[0] + dx * t, a[1] + dz * t]
  return { q, d2: (x - q[0]) ** 2 + (z - q[1]) ** 2 }
}

/** Closest boundary point over all contours, with the edge it lies on. */
export function nearestBoundary(contours: Contour[], x: number, z: number): { q: Pt; d: number; contour: number; edge: number } {
  let best = { q: [x, z] as Pt, d: Infinity, contour: 0, edge: 0 }
  contours.forEach((c, ci) => {
    for (let i = 0; i < c.length; i++) {
      const r = nearestOnSegment(c[i]!, c[(i + 1) % c.length]!, x, z)
      if (r.d2 < best.d * best.d) best = { q: r.q, d: Math.sqrt(r.d2), contour: ci, edge: i }
    }
  })
  return best
}

/** Distance to the region's boundary: negative inside the filled region, positive outside. */
export function signedDistance(contours: Contour[], x: number, z: number): number {
  const d = nearestBoundary(contours, x, z).d
  return pointInRegion(contours, x, z) ? -d : d
}

/** Parameters t ∈ (0,1) at which segment a→b crosses the edges of the contour. */
function crossings(c: Contour, ax: number, az: number, bx: number, bz: number): number[] {
  const out: number[] = []
  const dx = bx - ax
  const dz = bz - az
  for (let i = 0; i < c.length; i++) {
    const p = c[i]!
    const q = c[(i + 1) % c.length]!
    const ex = q[0] - p[0]
    const ez = q[1] - p[1]
    const den = dx * ez - dz * ex
    if (Math.abs(den) < 1e-14) continue
    const t = ((p[0] - ax) * ez - (p[1] - az) * ex) / den
    const u = ((p[0] - ax) * dz - (p[1] - az) * dx) / den
    if (t > 1e-9 && t < 1 - 1e-9 && u >= 0 && u < 1) out.push(t)
  }
  return out
}

/** How many edges of the contour the segment crosses. */
export function edgeCrossings(c: Contour, ax: number, az: number, bx: number, bz: number): number {
  return crossings(c, ax, az, bx, bz).length
}

/** Parametric spans of segment a→b (t ∈ [0,1]) that lie inside the even-odd region. */
export function segmentSpans(contours: Contour[], ax: number, az: number, bx: number, bz: number): [number, number][] {
  const ts = [0, 1]
  for (const c of contours) ts.push(...crossings(c, ax, az, bx, bz))
  ts.sort((p, q) => p - q)
  const spans: [number, number][] = []
  for (let i = 0; i + 1 < ts.length; i++) {
    const t0 = ts[i]!
    const t1 = ts[i + 1]!
    if (t1 - t0 < 1e-12) continue
    const tm = (t0 + t1) / 2
    if (!pointInRegion(contours, ax + (bx - ax) * tm, az + (bz - az) * tm)) continue
    const last = spans[spans.length - 1]
    if (last && Math.abs(last[1] - t0) < 1e-12) last[1] = t1
    else spans.push([t0, t1])
  }
  return spans
}

/**
 * Unit left-hand normal of edge i that points *out of the solid* of the region
 * (into the empty space), found by probing a point just beside the edge.
 */
export function outwardNormal(contours: Contour[], ci: number, i: number): Pt {
  const c = contours[ci]!
  const a = c[i]!
  const b = c[(i + 1) % c.length]!
  const dx = b[0] - a[0]
  const dz = b[1] - a[1]
  const l = Math.hypot(dx, dz) || 1
  const left: Pt = [-dz / l, dx / l]
  const mx = (a[0] + b[0]) / 2
  const mz = (a[1] + b[1]) / 2
  const probe = Math.max(1e-4, l * 1e-4)
  return pointInRegion(contours, mx + left[0] * probe, mz + left[1] * probe) ? [-left[0], -left[1]] : left
}

function segmentsIntersect(a: Pt, b: Pt, c: Pt, d: Pt): boolean {
  const o = (p: Pt, q: Pt, r: Pt) => (q[0] - p[0]) * (r[1] - p[1]) - (q[1] - p[1]) * (r[0] - p[0])
  const d1 = o(a, b, c)
  const d2 = o(a, b, d)
  const d3 = o(c, d, a)
  const d4 = o(c, d, b)
  return d1 * d2 < 0 && d3 * d4 < 0
}

/** At least 3 distinct points, non-zero area and no edge crossing another edge. */
export function isSimple(c: Contour): boolean {
  if (c.length < 3) return false
  if (Math.abs(signedArea(c)) < 1e-9) return false
  for (let i = 0; i < c.length; i++) {
    const p = c[i]!
    const q = c[(i + 1) % c.length]!
    if (Math.hypot(q[0] - p[0], q[1] - p[1]) < 1e-9) return false
    for (let j = i + 2; j < c.length; j++) {
      if (i === 0 && j === c.length - 1) continue // neighbouring edge through the wrap-around
      if (segmentsIntersect(p, q, c[j]!, c[(j + 1) % c.length]!)) return false
    }
  }
  return true
}

// ---- Shape library -------------------------------------------------------------------------------

/** Regular polygon inscribed in the unit square (radius 0.5). */
export function regular(n: number, rotation = 0): Contour {
  return Array.from({ length: n }, (_, i) => {
    const a = rotation + (i / n) * Math.PI * 2
    return [0.5 * Math.cos(a), 0.5 * Math.sin(a)] as Pt
  })
}

export const SHAPE_PRESETS = {
  rectangle: {
    label: 'Rectangle',
    points: (): Contour => [
      [-0.5, -0.5],
      [0.5, -0.5],
      [0.5, 0.5],
      [-0.5, 0.5],
    ],
  },
  l: {
    label: 'L shape',
    points: (): Contour => [
      [-0.5, -0.5],
      [0.5, -0.5],
      [0.5, 0],
      [0, 0],
      [0, 0.5],
      [-0.5, 0.5],
    ],
  },
  t: {
    label: 'T shape',
    points: (): Contour => [
      [-0.5, -0.5],
      [0.5, -0.5],
      [0.5, -0.15],
      [0.15, -0.15],
      [0.15, 0.5],
      [-0.15, 0.5],
      [-0.15, -0.15],
      [-0.5, -0.15],
    ],
  },
  u: {
    label: 'U shape',
    points: (): Contour => [
      [-0.5, -0.5],
      [-0.15, -0.5],
      [-0.15, 0.15],
      [0.15, 0.15],
      [0.15, -0.5],
      [0.5, -0.5],
      [0.5, 0.5],
      [-0.5, 0.5],
    ],
  },
  cross: {
    label: 'Cross',
    points: (): Contour => [
      [-0.17, -0.5],
      [0.17, -0.5],
      [0.17, -0.17],
      [0.5, -0.17],
      [0.5, 0.17],
      [0.17, 0.17],
      [0.17, 0.5],
      [-0.17, 0.5],
      [-0.17, 0.17],
      [-0.5, 0.17],
      [-0.5, -0.17],
      [-0.17, -0.17],
    ],
  },
  hexagon: { label: 'Hexagon', points: (): Contour => regular(6) },
  octagon: { label: 'Octagon', points: (): Contour => regular(8, Math.PI / 8) },
  triangle: { label: 'Triangle', points: (): Contour => regular(3, -Math.PI / 2) },
  star: {
    label: 'Star',
    points: (): Contour =>
      Array.from({ length: 10 }, (_, i) => {
        const a = -Math.PI / 2 + (i / 10) * Math.PI * 2
        const r = i % 2 === 0 ? 0.5 : 0.22
        return [r * Math.cos(a), r * Math.sin(a)] as Pt
      }),
  },
  circle: { label: 'Circle (24)', points: (): Contour => regular(24) },
} as const

export type ShapePresetId = keyof typeof SHAPE_PRESETS
